import { useAuth } from '@fourthwall/auth'
import { ConfirmModal, Toasts, useModalContext } from '@fourthwall/components'
import { CreatorIntegrationsApiClient } from '@fourthwall/services/dist/CreatorIntegrationsApiClient'
import type { CreatorIntegrationsApi } from '@fourthwall/services/dist/CreatorIntegrationsApiClient/models'
import type { AppName } from '@hooks'
import { useDeleteCreatorIntegrationMutation } from '@mutations'

import type { ConnectBarProps } from '../../../components/ConnectBar/ConnectBar'
import { useAppConnectEvent } from '../../../hooks/useAppConnectEvent'
import { useAppConnectedEvent } from '../../../hooks/useAppConnectedEvent'
import { useCreatorIntegrationsQuery } from '../../../queries/useCreatorIntegrationsQuery'

export const useIntegration = (props: {
  name: string
  logoSrc: string
  description: string
  id: keyof CreatorIntegrationsApi.GetCreatorIntegrations.Response
  returnUrl?: string
  recoveryActions?: {
    label: string
    permissions: string[]
  }[]
}) => {
  const { name, logoSrc, description, id, returnUrl, recoveryActions } = props
  const { keycloak } = useAuth()
  const { creatorIntegrationsQuery } = useCreatorIntegrationsQuery()
  const { open } = useModalContext()
  const { deleteCreatorIntegrationMutation } = useDeleteCreatorIntegrationMutation()
  const appConnectEvent = useAppConnectEvent(id as AppName)

  useAppConnectedEvent(id as AppName)

  const connectBarBase = {
    logoSrc,
    logoAlt: name,
    description,
    disabled: false,
  }

  const status = creatorIntegrationsQuery.data?.[id].status

  const updateAction = (
    missingScopes: string[],
    recoveryActions: {
      label: string
      permissions: string[]
    }[],
  ) => {
    for (const action of recoveryActions) {
      if (missingScopes.some((scope) => action.permissions.includes(scope))) {
        return {
          label: action.label,
          action: () => {
            appConnectEvent.trigger()
            const url = CreatorIntegrationsApiClient.appAuthorizationUrl({
              app: id,
              token: keycloak?.token,
            })
            window.location.assign(url)
          },
        }
      }
    }

    return undefined
  }

  const getConnectBar = (): ConnectBarProps | undefined => {
    // unknown
    if (!creatorIntegrationsQuery.data) return undefined

    // not connected
    switch (status) {
      case 'NOT_CONNECTED': {
        const onConnect = () => {
          appConnectEvent.trigger()

          const url = CreatorIntegrationsApiClient.appAuthorizationUrl({
            app: id,
            token: keycloak?.token,
            returnUrl,
          })

          window.location.assign(url)
        }

        return {
          ...connectBarBase,
          onConnect,
        }
      }

      case 'CONNECTED': {
        const onDisconnect = () => {
          open(ConfirmModal, {
            title: `Are you sure you want to disconnect from ${name}?`,
            confirmLabel: 'Yes, disconnect',
            confirmAppearance: 'destructive',
            onConfirm: () => {
              const toastId = `delete${name}Mutation`

              Toasts.notify(`Disconnecting from ${name}...`, {
                toastId,
                type: 'info',
                autoClose: false,
              })

              deleteCreatorIntegrationMutation.mutate(id, {
                onSuccess: () => {
                  creatorIntegrationsQuery.refetch()

                  Toasts.notify(`Disconnected from ${name} successfully.`, {
                    type: 'success',
                  })
                  Toasts.dismiss(toastId)
                },
              })
            },
          })
        }

        const update = recoveryActions
          ? updateAction(
              (creatorIntegrationsQuery.data[id] as any).missingScopes || [],
              recoveryActions,
            )
          : undefined

        return {
          ...connectBarBase,
          description: '',
          onDisconnect,
          onUpdate: update?.action,
          updateInfo: update?.label,
        }
      }
    }
  }

  return {
    status,
    connectBar: getConnectBar(),
  }
}
